import {
  BellIcon,
  BlocksIcon,
  BoxIcon,
  Building2Icon,
  ChartColumnIcon,
  CircleUserRoundIcon,
  FlagIcon,
  MapIcon,
  MapPinIcon,
  MonitorUpIcon,
  PackageIcon,
  ScanQrCodeIcon,
  SlidersVerticalIcon,
  TagIcon,
  TextAlignStartIcon,
  TruckIcon,
  UsersIcon,
  WrenchIcon,
} from 'lucide-react'
import { action, computed, makeObservable, observable } from 'mobx'

import {
  IHistoryItem,
  ILastUpdatedItem,
  IProjectHistory,
  IProjectInput,
} from '~/client/graph'
import { ISaveProjectHistoryMutation } from '~/client/graph/operations/generated/ProjectHistory.generated'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import DesktopCommonStore from '~/client/src/desktop/stores/ui/DesktopCommon.store'
import setUpPagesNames from '~/client/src/desktop/utils/setUpPagesNames'
import ISetUpStep from '~/client/src/desktop/views/ProjectSetUp/components/SetUpSteps/ISetUpStep'
import ProjectSetUpPage, {
  SETUP_PAGE_QUERY_KEY,
  SETUP_TAB_QUERY_KEY,
} from '~/client/src/desktop/views/ProjectSetUp/ProjectSetUpPages'
import { LOCATION_ICON_COMPONENTS } from '~/client/src/shared/components/SitemapHelpers/utils/markerSpec'
import * as TagIcons from '~/client/src/shared/components/TagIcon'
import { GenericWorkflowIcon } from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowIconGroups'
import { IntegrationAppName } from '~/client/src/shared/enums/IntegrationAppName'
import { LocalStorageKey } from '~/client/src/shared/enums/LocalStorageKey'
import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Project from '~/client/src/shared/models/Project'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import Guard from '~/client/src/shared/utils/Guard'
import LocalStorageHelper from '~/client/src/shared/utils/LocalStorageHelper'

import desktopRoutes from '../../constants/desktopRoutes'
import ISaveProjectSuccessOptions from '../../interfaces/ISaveProjectSuccessOptions'
import { LocationsTab } from './components/Locations/LocationsSetupTabs'

const {
  DEFAULT,

  EDIT_PROFILE,
  NOTIFICATION_SETTINGS,

  USER_DIRECTORY,
  COMPANY_DIRECTORY,
  USER_TAGS,

  PROJECT_DETAILS,
  PROJECT_CREATION,
  MAP_SETUP,
  PRESENTATION_MODE,
  INTEGRATIONS,
  ANALYTICS,
  MATERIALS_UPLOAD,
  MATERIALS,
  EQUIPMENT,
  LOCATIONS,
  STRUXTAGS,
  FORM_WORKFLOWS,
  FORM_NOTIFICATIONS,

  DELIVERY_WORKFLOWS,
  DELIVERY_LOCATIONS,
  DELIVERY_CARDS,
  DELIVERY_NOTIFICATIONS,
  QR_SCANNERS_SETUP,

  TEAM_ASSIGNMENT,
  APPS_CONNECTION,
} = ProjectSetUpPage
const setUpSteps = new Set([
  PROJECT_DETAILS,
  PROJECT_CREATION,
  USER_DIRECTORY,
  TEAM_ASSIGNMENT,
  PRESENTATION_MODE,
  DELIVERY_WORKFLOWS,
  MAP_SETUP,
  QR_SCANNERS_SETUP,
  APPS_CONNECTION,
  DELIVERY_NOTIFICATIONS,
  FORM_WORKFLOWS,
  FORM_NOTIFICATIONS,
  COMPANY_DIRECTORY,
  USER_TAGS,
  DELIVERY_LOCATIONS,
  DELIVERY_CARDS,
  INTEGRATIONS,
  ANALYTICS,
  MATERIALS_UPLOAD,
  MATERIALS,
  EQUIPMENT,
  LOCATIONS,
  STRUXTAGS,
])

// Ids of retired setup pages that old bookmarks may still carry, and the page + tab
// that replaced each one. Anything not listed falls back to DEFAULT like any unknown id.
const RETIRED_PAGE_REDIRECTS = new Map<
  string,
  { page: ProjectSetUpPage; tab: string }
>([
  // The legacy Location Controls page; the Locations app's Controls tab replaced it.
  ['location-controls', { page: LOCATIONS, tab: LocationsTab.Controls }],
])

export enum ProjectDetailsTabs {
  Details = 'Details',
  Time = 'Time',
  SharedWorkflows = 'Shared Workflows',
  Apps = 'Apps',
  DomainsAndSignUp = 'Domains & Sign Up',
  QRCodes = 'QR Codes',
}

export default class ProjectSetUpPageStore {
  // State for the Sidebar when in Settings
  @observable public isSettingsSidebarCollapsed: boolean = true

  // State for the Sidebar when NOT in Settings
  @observable public isSharedSidebarCollapsed: boolean = true

  @observable private _showSidebarOutsideOfSettings: boolean = null
  @observable public activeTab: ProjectDetailsTabs = ProjectDetailsTabs.Details

  public get isHistoryLoading() {
    return this.state.loading.get(e.LOAD_AND_LISTEN_TO_PROJECT_HISTORY)
  }

  private get projectHistory() {
    return this.state.projectHistory
  }

  public readonly usersSettingsPages: ISetUpStep[]
  public readonly viewModeSettingsPages: ISetUpStep[]
  public readonly projectSettingsPages: ISetUpStep[]
  public readonly locationSettingsPages: ISetUpStep[]
  public readonly analyticsSettingsPages: ISetUpStep[]
  public readonly deliveriesSettingsPages: ISetUpStep[]
  public readonly companiesSettingsPages: ISetUpStep[]
  public readonly formsSettingsPages: ISetUpStep[]
  public readonly scannerSettingsPages: ISetUpStep[]
  public readonly integrationSettingsPages: ISetUpStep[]
  public readonly materialsSettingsPages: ISetUpStep[]
  public readonly materialResourcesSettingsPages: ISetUpStep[]
  public readonly equipmentSettingsPages: ISetUpStep[]
  public readonly locationResourcesSettingsPages: ISetUpStep[]

  @observable private _page: ProjectSetUpPage = DEFAULT

  public readonly accountSettingsPages: ISetUpStep[]
  public constructor(
    private eventsStore: DesktopEventStore,
    private common: DesktopCommonStore,
    private readonly projectMembersStore: ProjectMembersStore,
  ) {
    Guard.againstUndefined(eventsStore, 'eventsStore')
    Guard.againstUndefined(common, 'common')

    this.integrationSettingsPages = [
      {
        page: INTEGRATIONS,
        getTitle: () => Localization.translator.integrations,
        Icon: BlocksIcon,
      },
    ]

    this.projectSettingsPages = [
      {
        page: PROJECT_DETAILS,
        getTitle: () => Localization.translator.projectDetails,
        Icon: FlagIcon,
      },
    ]
    this.viewModeSettingsPages = [
      {
        page: PRESENTATION_MODE,
        getTitle: () => Localization.translator.presentationMode,
        Icon: MonitorUpIcon,
      },
    ]
    this.locationSettingsPages = [
      {
        page: MAP_SETUP,
        getTitle: () => Localization.translator.maps,
        Icon: MapIcon,
      },
    ]
    this.materialsSettingsPages = [
      {
        page: MATERIALS_UPLOAD,
        getTitle: () => Localization.translator.materialsLegacy,
        Icon: BoxIcon,
      },
    ]
    this.materialResourcesSettingsPages = [
      {
        page: MATERIALS,
        getTitle: () => Localization.translator.materials,
        Icon: PackageIcon,
      },
    ]
    this.equipmentSettingsPages = [
      {
        page: EQUIPMENT,
        getTitle: () => Localization.translator.equipment,
        Icon: WrenchIcon,
      },
    ]
    this.locationResourcesSettingsPages = [
      {
        page: LOCATIONS,
        getTitle: () => Localization.translator.locations,
        Icon: MapPinIcon,
      },
    ]
    this.formsSettingsPages = [
      {
        page: FORM_WORKFLOWS,
        getTitle: () => Localization.translator.configurator,
        Icon: GenericWorkflowIcon,
      },
      {
        page: FORM_NOTIFICATIONS,
        getTitle: () => Localization.translator.notifications,
        Icon: BellIcon,
      },
    ]
    this.usersSettingsPages = [
      {
        page: USER_DIRECTORY,
        getTitle: () => Localization.translator.directory,
        Icon: TagIcons.User,
      },
      {
        page: USER_TAGS,
        getTitle: () => Localization.translator.tags,
        Icon: TagIcon,
      },
    ]
    this.deliveriesSettingsPages = [
      {
        page: DELIVERY_WORKFLOWS,
        getTitle: () => Localization.translator.workflows,
        Icon: TruckIcon,
      },
      {
        page: DELIVERY_LOCATIONS,
        getTitle: () => Localization.translator.locations,
        Icon: LOCATION_ICON_COMPONENTS[MapViewLocationIcon.Zone],
      },
      {
        page: DELIVERY_CARDS,
        getTitle: () => Localization.translator.cards,
        Icon: TextAlignStartIcon,
      },
      {
        page: DELIVERY_NOTIFICATIONS,
        getTitle: () => Localization.translator.notifications,
        Icon: BellIcon,
      },
    ]
    this.accountSettingsPages = [
      {
        page: EDIT_PROFILE,
        getTitle: () => Localization.translator.profile,
        Icon: CircleUserRoundIcon,
      },
      {
        page: NOTIFICATION_SETTINGS,
        getTitle: () => Localization.translator.preferences,
        Icon: SlidersVerticalIcon,
      },
    ]
    this.companiesSettingsPages = [
      {
        page: COMPANY_DIRECTORY,
        getTitle: () => Localization.translator.companies,
        Icon: Building2Icon,
      },
    ]
    this.scannerSettingsPages = [
      {
        page: QR_SCANNERS_SETUP,
        getTitle: () => Localization.translator.qrScannersSetup,
        Icon: ScanQrCodeIcon,
      },
    ]
    this.analyticsSettingsPages = [
      {
        page: ANALYTICS,
        getTitle: () => Localization.translator.analyticsSetting,
        Icon: ChartColumnIcon,
      },
    ]

    makeObservable(this)
  }

  public async saveCurrentProjectHistory() {
    this.state.projectHistory = await this.saveProjectHistory(
      this.projectHistory,
    )
  }

  public async saveProjectHistory(
    projectHistory: IProjectHistory,
    shouldCompleteRequest?: boolean,
  ): Promise<IProjectHistory> {
    const input = JSON.parse(JSON.stringify(projectHistory)) as IProjectHistory
    input.lastUpdated?.forEach(lu => delete lu.updateInfo.schedule)
    input.scheduleHistory?.forEach(h => delete h.schedule)
    input.materialsHistory?.forEach(h => delete h.schedule)

    return new Promise((resolve, reject) => {
      this.eventsStore.dispatch(
        e.SAVE_PROJECT_HISTORY,
        input,
        (data: ISaveProjectHistoryMutation) =>
          resolve(data.saveProjectHistory as IProjectHistory),
        reject,
        shouldCompleteRequest,
      )
    })
  }

  public setPageLastUpdated(lastUpdatedItem: ILastUpdatedItem) {
    const newValue = this.lastUpdated.filter(
      i => i.pageName !== lastUpdatedItem.pageName,
    )
    if (lastUpdatedItem.updateInfo) {
      newValue.push(lastUpdatedItem)
    }
    this.projectHistory.lastUpdated = newValue
  }

  public get state(): DesktopInitialState {
    return this.eventsStore.appState
  }

  public get currentPage(): ProjectSetUpPage {
    return this._page
  }

  public set currentPage(value: ProjectSetUpPage) {
    this.setCurrentPage(value)
  }

  public get displayAccountSettingsPages(): ISetUpStep[] {
    return [...this.accountSettingsPages]
  }

  public get legacySettingsPages(): ISetUpStep[] {
    const legacySettingsPages: ISetUpStep[] = []
    if (!this.isDeliveriesDisabled) {
      legacySettingsPages.push({
        getTitle: () => Localization.translator.deliveries,
        Icon: TruckIcon,
        substeps: this.deliveriesSettingsPages,
      })
    }

    if (!this.isMaterialsDisabled) {
      legacySettingsPages.push(...this.materialsSettingsPages)
    }

    return legacySettingsPages
  }

  public get resourcesSettingsPages(): ISetUpStep[] {
    const resourcesSettingsPages: ISetUpStep[] = [
      {
        getTitle: () => Localization.translator.users,
        Icon: UsersIcon,
        substeps: this.usersSettingsPages,
      },
      ...this.companiesSettingsPages,
    ]

    if (!this.isLocationsDisabled) {
      resourcesSettingsPages.push(...this.locationResourcesSettingsPages)
    }
    if (!this.isEquipmentDisabled) {
      resourcesSettingsPages.push(...this.equipmentSettingsPages)
    }
    if (!this.isMaterialResourcesDisabled) {
      resourcesSettingsPages.push(...this.materialResourcesSettingsPages)
    }

    return resourcesSettingsPages
  }

  public get moreSettingsPages(): ISetUpStep[] {
    const moreSettingsPages: ISetUpStep[] = []
    if (!this.isAnalyticsDisabled) {
      moreSettingsPages.push(...this.analyticsSettingsPages)
    }
    if (this.isScanMaster) {
      moreSettingsPages.push(...this.scannerSettingsPages)
    }
    moreSettingsPages.push(...this.viewModeSettingsPages)

    return moreSettingsPages
  }

  @action.bound
  private setCurrentPage(value: ProjectSetUpPage) {
    if (Object.values(ProjectSetUpPage).includes(value)) {
      this._page = value
      return
    }
    this._page = DEFAULT
  }

  @action.bound
  public setSidebarCollapsed(isHidden: boolean) {
    // Keep both sidebar states in sync
    this.isSettingsSidebarCollapsed = isHidden
    this.isSharedSidebarCollapsed = isHidden
  }

  @action.bound
  public toggleShowSidebarOutsideOfSettings() {
    this._showSidebarOutsideOfSettings = !this._showSidebarOutsideOfSettings

    if (this.state?.user?.id) {
      LocalStorageHelper.setUserPreferences(this.state.user.id, {
        [LocalStorageKey.AlwaysShowSettingsSidebar]: this
          ._showSidebarOutsideOfSettings
          ? 'true'
          : 'false',
      })
    }
  }

  // Lazy load from local storage
  public get showSidebarOutsideOfSettings(): boolean {
    if (this._showSidebarOutsideOfSettings === null && this.state?.user?.id) {
      const raw = LocalStorageHelper.getUserPreferenceByKey(
        this.state.user.id,
        LocalStorageKey.AlwaysShowSettingsSidebar,
      )
      this._showSidebarOutsideOfSettings = raw !== 'false'
    }

    // Ensure return is boolean even if user is not yet defined
    return !!this._showSidebarOutsideOfSettings
  }

  @action
  public applyPageFromQueryParams(search: string) {
    const queryParams = new URLSearchParams(search)
    const requestedPage = queryParams.get(SETUP_PAGE_QUERY_KEY)

    const replacement = RETIRED_PAGE_REDIRECTS.get(requestedPage)
    if (
      replacement &&
      this.isAdmin &&
      this.isAllowedToNavigate(replacement.page)
    ) {
      // Replace rather than push (and skip navigateTo's same-page guard) so Back never
      // returns to the retired id, and the landing holds even when already on the page.
      const searchParams = new URLSearchParams({
        [SETUP_PAGE_QUERY_KEY]: replacement.page,
        [SETUP_TAB_QUERY_KEY]: replacement.tab,
      })
      this.common.history.replace(
        `${this.common.history.location.pathname}?${searchParams.toString()}`,
      )
      this.currentPage = replacement.page
      return
    }

    this.currentPage = requestedPage as ProjectSetUpPage

    this.redirectIfNotAllowed()
  }

  public get nextPage(): ProjectSetUpPage {
    if (!this.isSetUpPage(this.currentPage)) {
      return this.currentPage
    }
    const nextPage = this.allProjectSettingsList.find(
      page => +page.page > +this.currentPage,
    )
    return nextPage ? nextPage.page : this.currentPage
  }

  @computed
  private get allProjectSettingsList(): ISetUpStep[] {
    const allProjectSettingsList = [
      ...this.projectSettingsPages,
      ...this.companiesSettingsPages,
      ...this.locationSettingsPages,
      ...this.usersSettingsPages,
      ...this.viewModeSettingsPages,
    ]
    if (!this.isEquipmentDisabled) {
      allProjectSettingsList.push(...this.equipmentSettingsPages)
    }
    if (!this.isLocationsDisabled) {
      allProjectSettingsList.push(...this.locationResourcesSettingsPages)
    }
    if (this.isIntegrationsEnabled) {
      allProjectSettingsList.push(...this.integrationSettingsPages)
    }
    if (!this.isMaterialResourcesDisabled) {
      allProjectSettingsList.push(...this.materialResourcesSettingsPages)
    }
    if (!this.isMaterialsDisabled) {
      allProjectSettingsList.push(...this.materialsSettingsPages)
    }
    if (!this.isFormsDisabled) {
      allProjectSettingsList.push(...this.formsSettingsPages)
    }
    if (this.hasScanAccess) {
      allProjectSettingsList.push(...this.scannerSettingsPages)
    }
    if (!this.isDeliveriesDisabled) {
      allProjectSettingsList.push(...this.deliveriesSettingsPages)
    }
    if (!this.isAnalyticsDisabled) {
      allProjectSettingsList.push(...this.analyticsSettingsPages)
    }

    return allProjectSettingsList
  }

  public get currentUser() {
    return this.state.user
  }

  public get hasScanAccess(): boolean {
    return this.state.userActiveProjectSettings?.hasScanAccess
  }

  public get currentProject(): Project {
    return this.state.activeProject
  }

  public get isAdmin(): boolean {
    return this.state.userActiveProjectSettings?.isAdmin
  }

  public get isScanMaster(): boolean {
    return this.isAdmin || this.state.userActiveProjectSettings?.isScanMaster
  }

  public get lastUpdated(): ILastUpdatedItem[] {
    return this.projectHistory.lastUpdated || []
  }

  public getLastPageUpdate(pageName: string): IHistoryItem {
    return this.lastUpdated.find(u => u.pageName === pageName)?.updateInfo
  }

  public get materialsHistory(): IHistoryItem[] {
    return this.projectHistory.materialsHistory || []
  }

  public get projectMembersQuantity(): number {
    return this.projectMembersStore.list.length
  }

  public redirectIfNotAllowed() {
    if (
      !this.isSetUpPage(this.currentPage) ||
      this.isAllowedToNavigate(this.currentPage)
    ) {
      return
    }

    let pageIndex = +this.currentPage

    // TODO: rewrite it
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (isNaN(pageIndex)) {
        return this.navigateTo(ProjectSetUpPage.EDIT_PROFILE)
      }

      const page = String(--pageIndex) as ProjectSetUpPage

      if (this.isAllowedToNavigate(page)) {
        return this.navigateTo(page)
      }
    }
  }

  public navigateTo(
    pageToNavigateTo: ProjectSetUpPage,
    additionalParams?: { [key: string]: string },
  ) {
    if (
      pageToNavigateTo !== this.currentPage &&
      (pageToNavigateTo === ProjectSetUpPage.DEFAULT ||
        this.isAllowedToNavigate(pageToNavigateTo))
    ) {
      const searchParams = new URLSearchParams(
        Object.assign(
          { [SETUP_PAGE_QUERY_KEY]: pageToNavigateTo },
          additionalParams || {},
        ) as any,
      )

      this.common._displayView(
        `${desktopRoutes.PROJECT_SETUP}?${searchParams.toString()}`,
      )
    }
  }

  public navigateToNextStep() {
    this.navigateTo(this.nextPage)
  }

  public isAllowedToNavigate(page: ProjectSetUpPage): boolean {
    if (
      page === ProjectSetUpPage.EDIT_PROFILE ||
      page === ProjectSetUpPage.NOTIFICATION_SETTINGS ||
      page === ProjectSetUpPage.PROJECT_CREATION
    ) {
      return true
    }

    const dependencies = this.pagesDependencies[page] || []
    const areDependenciesCompleted = dependencies.every(dependency =>
      this.isPageCompleted(dependency),
    )

    const isPageAllowed =
      this.isSetUpPage(page) &&
      this.allowedProjectSettingsList.some(item => item.page === page)
    return areDependenciesCompleted && isPageAllowed
  }

  private get allowedProjectSettingsList(): ISetUpStep[] {
    return this.allProjectSettingsList
  }

  public getProjectSetUpPageName(page: ProjectSetUpPage): string {
    if (!Object.keys(setUpPagesNames).includes(page)) {
      return page as string
    }
    return setUpPagesNames[page]
  }

  public isSetUpPage(page: ProjectSetUpPage): boolean {
    return setUpSteps.has(page)
  }

  public isPageCompleted(page: ProjectSetUpPage): boolean {
    const pageName = this.getProjectSetUpPageName(page)
    const { user, activeProject } = this.state

    const accountSettingsCompleted = user?.setUpFinished || {}
    const projectSettingsCompleted = activeProject.setUpFinished
    return (
      !!accountSettingsCompleted[pageName] ||
      projectSettingsCompleted?.includes(pageName)
    )
  }

  public setCurrentPageAsCompletedOnProject(
    filename?: string,
    scheduleId?: string,
  ): Promise<any> {
    if (!setUpSteps.has(this.currentPage)) {
      return Promise.resolve()
    }

    const pageName = this.getProjectSetUpPageName(this.currentPage)

    const setUpFinished = this.currentProject.setUpFinished || []
    if (!setUpFinished.includes(pageName)) {
      setUpFinished.push(pageName)
    }
    this.currentProject.setUpFinished = setUpFinished

    const { id, email, firstName = null, lastName = null } = this.state.user

    this.setPageLastUpdated({
      pageName,
      updateInfo: {
        updatedAt: Date.now(),
        fileName: filename || null,
        scheduleId: scheduleId || null,
        by: { id, email, firstName, lastName },
      },
    })

    return this.saveCurrentProject().then(() =>
      this.saveCurrentProjectHistory(),
    )
  }

  public async saveCurrentProject(projectInput?: IProjectInput) {
    return new Promise((resolve, reject) => {
      this.eventsStore.dispatch(
        e.SAVE_PROJECT,
        projectInput || this.currentProject,
        { onSuccessCb: resolve } as ISaveProjectSuccessOptions,
        reject,
      )
    })
  }

  // if page doesn't have dependings list it is available always
  private get pagesDependencies() {
    return {
      [USER_DIRECTORY]: [PROJECT_DETAILS],
      [COMPANY_DIRECTORY]: [PROJECT_DETAILS],
      [USER_TAGS]: [PROJECT_DETAILS],
      [PRESENTATION_MODE]: [PROJECT_DETAILS],
      [DELIVERY_WORKFLOWS]: [PROJECT_DETAILS],
      [DELIVERY_LOCATIONS]: [PROJECT_DETAILS],
      [DELIVERY_CARDS]: [PROJECT_DETAILS],
      [MAP_SETUP]: [PROJECT_DETAILS],
      [DELIVERY_NOTIFICATIONS]: [PROJECT_DETAILS],
    }
  }

  public get isDeliveriesDisabled(): boolean {
    return this.state.isDeliveriesDisabled
  }

  public get isAnalyticsDisabled(): boolean {
    return this.state.isAnalyticsDisabled
  }

  public get isIntegrationsEnabled(): boolean {
    return (
      this.eventsStore.appState.isIntegrationEnabled(
        IntegrationAppName.MATURIX,
      ) ||
      this.eventsStore.appState.isIntegrationEnabled(
        IntegrationAppName.CONCRETE_DIRECT,
      ) ||
      this.eventsStore.appState.isIntegrationEnabled(IntegrationAppName.PROCORE)
    )
  }

  public get isFormsDisabled(): boolean {
    return this.state.isWorkflowsDisabled
  }

  public get isMaterialsDisabled(): boolean {
    return this.state.isMaterialsDisabled
  }

  public get isMaterialResourcesDisabled(): boolean {
    return this.state.isMaterialResourcesDisabled
  }

  public get isEquipmentDisabled(): boolean {
    return this.state.isEquipmentDisabled
  }

  public get isLocationsDisabled(): boolean {
    return this.state.isLocationsDisabled
  }

  public getKeyForStep(step: ISetUpStep): string {
    if (step.page) {
      return step.page
    }

    if (step.substeps && Array.isArray(step.substeps) && step.substeps.length) {
      return step.substeps.reduce((key, substep) => {
        return key + this.getKeyForStep(substep)
      }, '')
    }

    return step.getTitle()
  }
}
